import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { observeWhenVisible } from 'Utils/performance/sharedIntersectionObserver';

type Options = {
  enabled?: boolean;
  rootMargin?: string;
};

/**
 * Defers an image URL until the target element is near the viewport.
 * Uses one shared IntersectionObserver for all instances.
 */
export function useLazyImageSrc(
  src: string | undefined,
  options: Options = {},
) {
  const { enabled = true, rootMargin = '200px 0px' } = options;
  const [node, setNode] = useState<HTMLElement | null>(null);
  const loadedSrcRef = useRef<string | null>(null);
  const [activeSrc, setActiveSrc] = useState(() => (
    enabled ? '' : (src ?? '')
  ));

  const ref = useCallback((element: HTMLElement | null) => {
    setNode(element);
  }, []);

  useEffect(() => {
    if (!enabled) {
      loadedSrcRef.current = src ?? '';
      setActiveSrc(src ?? '');
      return undefined;
    }

    if (!src) {
      loadedSrcRef.current = null;
      setActiveSrc('');
      return undefined;
    }

    if (loadedSrcRef.current === src) {
      setActiveSrc(src);
      return undefined;
    }

    if (!node) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      loadedSrcRef.current = src;
      setActiveSrc(src);
      return undefined;
    }

    return observeWhenVisible(
      node,
      () => {
        loadedSrcRef.current = src;
        setActiveSrc(src);
      },
      rootMargin,
    );
  }, [enabled, node, rootMargin, src]);

  return { ref, src: activeSrc };
}
